import React, { useState, useRef } from "react";
import { Modal, Input, Button, Upload, Spin, Select } from "antd";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import "./style.scss";
import { SessionService } from "../../../base/services/session.service";
import Toast from "../../../base/components/Toast";
import { httpService } from "../../../base/services/httpService.service";
import { Asterisk, FileImport, XIcon } from "../../../assets/icons";
import { useNotification } from "../../../base/hooks/useNotification";
import AuthService from "../../../base/services/authentication.service";
import { useTranslation } from "../../../i18n/LanguageContext";

const { TextArea } = Input;

const formInitialValues = {
   title: "",
   text: "",
   fileUrls: [],
   email: "",
   ticketTypeId: []
};

const AddTicketModal = ({ visible, onCancel, onOk, fetchData, ticketType }) => {
   const { t } = useTranslation();
   const validationSchema = Yup.object().shape({
      title: Yup.string().required(t("title-required-validation")),
      ticketTypeId: Yup.string().required(t("ticket-type-required-validation")),
      text: Yup.string().required(t("message-required-validation"))
   });
   const formikRef = useRef(null);
   const workspaceId = SessionService.getItem("workspaceID");
   const [fileList, setFileList] = useState([]);
   const [loading, setLoading] = useState(false);
   const [selectedTicket, setSelectedTicket] = useState([]);
   const { api: notificationApi, notificationContextHolder } = useNotification();
   const user = AuthService?.getUser();

   const handleTickets = (selectedOption, values) => {
      if (selectedOption) {
         values.ticketTypeId = selectedOption;
         setSelectedTicket(selectedOption);
      }
   };

   const SubmitNewTicket = async (values, actions) => {
      const formData = new FormData();
      formData.append("workspaceId", workspaceId);
      formData.append("title", values?.title);
      formData.append("email", user?.email);
      formData.append("ticketTypeId", values?.ticketTypeId);
      formData.append("Message.Text", values?.text);

      fileList.forEach((file) => {
         formData.append("Message.FileUrls", file?.originFileObj);
      });
      try {
         setLoading(true);
         await httpService.post("/Tickets", formData, () => {
            notificationApi.success("ticket", {
               message: t("ticket-added-success-title"),
               description: t("ticket-added-success-description")
            });
            onOk();
            fetchData();
            actions.resetForm();
            setFileList([]);
            setSelectedTicket([]);
         });
      } catch (error) {
         notificationApi.error("ticket", {
            message: t("toast-oops-title"),
            description: t("add-ticket-error-description")
         });
      } finally {
         actions.setSubmitting(false);
         setLoading(false);
      }
   };

   const handleFileChange = ({ fileList }) => {
      const allowedTypes = ["image/png", "image/jpeg", "image/gif", "image/webp"];
      const filteredList = fileList?.filter((file) => allowedTypes.includes(file?.type));

      if (filteredList?.length !== fileList?.length) {
         Toast.error(t("upload-jpg-png-gif-webp-error"));
      }

      setFileList(filteredList.slice(0, 3));

      const attachmentIds = fileList?.slice(0, 3).map((file) => file?.originFileObj);
      formikRef.current.setFieldValue("fileUrls", attachmentIds);
   };

   const onCancelModal = () => {
      setSelectedTicket([]);
      setFileList([]);
      onCancel();
   };

   return (
      <>
         {notificationContextHolder}
         <Modal
            open={visible}
            onCancel={loading ? null : onCancelModal}
            onOk={SubmitNewTicket}
            closable={false}
            maskClosable={!loading}
            className="modalAddWorkspaceStyle"
            footer={[]}
            destroyOnClose
         >
            <img
               src={XIcon}
               alt="BlackXIcon"
               onClick={loading ? null : onCancelModal}
               style={{ float: "right", cursor: "pointer" }}
            />
            <h1 className="title-assessment-modal">{t("add-new-ticket-title")}</h1>
            <div className="admin-workspace">
               <div className="top-form">
                  <div className="login-form login-signin" style={{ display: "block" }}>
                     <Formik
                        innerRef={formikRef}
                        onSubmit={(values, actions) => {
                           SubmitNewTicket(values, actions);
                        }}
                        initialValues={formInitialValues}
                        validationSchema={validationSchema}
                     >
                        {({
                           handleSubmit,
                           handleChange,
                           touched,
                           values,
                           errors,
                           isSubmitting
                        }) => (
                           <Form>
                              <div>
                                 <div className="label-name-assessment-modal">
                                    <img src={Asterisk} alt="Asterisk" />
                                    <label>{t("email")}</label>
                                 </div>
                                 <Input
                                    value={user?.email}
                                    disabled
                                    style={{ height: "37px", borderRadius: "4px" }}
                                 />
                              </div>
                              <div>
                                 <div className="label-name-assessment-modal">
                                    <img src={Asterisk} alt="Asterisk" />
                                    <label>{t("ticket-type-label")}</label>
                                 </div>
                                 <Select
                                    placeholder={t("select-ticket-type-placeholder")}
                                    value={selectedTicket}
                                    name="ticketTypeId"
                                    onChange={(selectedTicket) => {
                                       handleTickets(selectedTicket, values);
                                    }}
                                    options={ticketType}
                                    values={values.ticketTypeId}
                                    style={{ width: "100%" }}
                                 />
                                 {errors.ticketTypeId && touched.ticketTypeId && (
                                    <div className="error-message">{errors.ticketTypeId}</div>
                                 )}{" "}
                              </div>
                              <div>
                                 <div className="label-name-assessment-modal">
                                    <img src={Asterisk} alt="Asterisk" />
                                    <label>{t("summary-label")}</label>
                                 </div>
                                 <Input
                                    autoComplete="off"
                                    type="text"
                                    name="title"
                                    onChange={handleChange}
                                    value={values?.title}
                                    placeholder={t("enter-title-placeholder")}
                                    style={{ height: "37px", borderRadius: "4px" }}
                                 />
                                 {errors.title && touched.title && (
                                    <div className="error-message">{errors.title}</div>
                                 )}
                              </div>

                              <div>
                                 <div className="label-name-assessment-modal">
                                    <img src={Asterisk} alt="Asterisk" />
                                    <label>{t("describe-problem-detail-label")}</label>
                                 </div>
                                 <TextArea
                                    type="text"
                                    name="text"
                                    placeholder={t("type-something-placeholder")}
                                    value={values?.message}
                                    onChange={handleChange}
                                    style={{ border: "2px solid #efefef", borderRadius: "4px" }}
                                 ></TextArea>
                                 <span
                                    style={{ float: "right", fontSize: "12px", color: "#979797" }}
                                 >
                                    {values.text.length}/100
                                 </span>

                                 {errors.text && touched.text && (
                                    <div className="text-danger">{errors.text}</div>
                                 )}
                              </div>
                              <div className="uploader-survey">
                                 <div className="label-name-assessment-modal">
                                    <label>{t("attach-evidence-label")}</label>
                                 </div>
                                 <Upload
                                    name="fileUrls"
                                    listType="picture-card"
                                    className="avatar-uploader-survey"
                                    showUploadList={true}
                                    multiple
                                    fileList={fileList}
                                    onChange={handleFileChange}
                                    beforeUpload={(file) => {
                                       if (fileList?.length < 3) {
                                          setFileList([...fileList, file]);
                                       }
                                       return false;
                                    }}
                                    accept=".png, .jpeg, .jpg, .gif, .webp"
                                 >
                                    <div className="uploading-survey">
                                       <img src={FileImport} alt="avatar" />
                                       <div>
                                          <div
                                             style={{
                                                color: "#4158CF",
                                                fontWeight: 600,
                                                fontSize: "16px"
                                             }}
                                          >
                                             {t("drag-drop-file-here-prefix")}{" "}
                                             <span style={{ textDecoration: "underline" }}>
                                                {t("click-to-select-file-text")}
                                             </span>
                                          </div>
                                          <div
                                             className="xlsx-file"
                                             style={{
                                                color: "#777777",
                                                fontWeight: 600,
                                                fontSize: "14px"
                                             }}
                                          >
                                             {t("jpg-png-gif-webp-text")}
                                          </div>
                                          <div
                                             className="max-size-file"
                                             style={{
                                                color: "#777777",
                                                fontSize: "14px"
                                             }}
                                          >
                                             {t("max-file-size-text")}
                                          </div>
                                       </div>
                                    </div>
                                 </Upload>
                              </div>
                              <div className="d-flex justify-content-around">
                                 <div className="footer-buttons">
                                    <Button
                                       className="cencel-add-assessment-button"
                                       key="cancel"
                                       onClick={loading ? null : onCancelModal}
                                       disabled={isSubmitting || loading}
                                    >
                                       {t("cancel")}
                                    </Button>
                                    <Button
                                       className="add-assessment-button"
                                       key="success"
                                       // type="submit"
                                       onClick={handleSubmit}
                                       disabled={isSubmitting || loading}
                                    >
                                       {loading ? <Spin /> : t("add-ticket-button")}
                                    </Button>
                                 </div>
                              </div>
                           </Form>
                        )}
                     </Formik>
                  </div>
               </div>
            </div>
         </Modal>
      </>
   );
};

export default AddTicketModal;
